import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SituViewReadonlyComponent } from '../../../render-product/situ-view-readonly/situ-view-readonly.component';

export interface SituReelStartViewPickerData {
  artifactId: string;
  currentStartViewQuery?: string;
}

// Resolves to:
//   - string  → new/updated query string to save (e.g. "panorama=kitchen-navpoint-0&hlookat=-108&vlookat=8")
//   - null    → user explicitly cleared the override
//   - undefined → user cancelled, no change
export type SituReelStartViewPickerResult = string | null | undefined;

@Component({
  selector: 'situ-reel-start-view-picker-dialog',
  templateUrl: './situ-reel-start-view-picker-dialog.component.html',
  styleUrls: ['./situ-reel-start-view-picker-dialog.component.scss']
})
export class SituReelStartViewPickerDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild(SituViewReadonlyComponent) readonly viewer?: SituViewReadonlyComponent;
  @ViewChild('modelContainer') modelContainer?: ElementRef<HTMLElement>;

  routeParams: { [key: string]: string } = {};
  pastedUrl = '';

  viewerWidth = 720;
  viewerHeight = 480;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SituReelStartViewPickerData,
    private dialogRef: MatDialogRef<SituReelStartViewPickerDialogComponent, SituReelStartViewPickerResult>,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {
    this.routeParams = this.buildInitialParams();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.calculateViewerSize(), 0);
  }

  ngOnDestroy(): void {}

  @HostListener('window:resize')
  onResize(): void {
    this.calculateViewerSize();
  }

  applyPastedUrl(): void {
    const parsed = this.parseQueryFromUrl(this.pastedUrl);
    if (!parsed) {
      this.snackBar.open('Could not read a panorama URL from that link.', 'OK', { duration: 2500 });
      return;
    }
    this.routeParams = { id: this.data.artifactId, quickview: 'true', ...parsed };
  }

  captureCurrentView(): void {
    if (!this.viewer) {
      this.snackBar.open('Viewer not ready yet.', 'OK', { duration: 2000 });
      return;
    }
    const panorama = this.viewer.selectedPanorama;
    if (!panorama) {
      this.snackBar.open('Navigate into a room first, then try again.', 'OK', { duration: 2500 });
      return;
    }
    const query = this.composeQuery(panorama, this.viewer.viewingAngle, this.viewer.verticalViewingAngle);
    this.dialogRef.close(query);
  }

  clearOverride(): void {
    this.dialogRef.close(null);
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  private calculateViewerSize(): void {
    if (!this.modelContainer) return;
    const rect = this.modelContainer.nativeElement.getBoundingClientRect();
    this.viewerWidth = Math.max(Math.floor(rect.width) - 4, 480);
    this.viewerHeight = Math.max(Math.floor(rect.height) - 4, 320);
    this.cdr.detectChanges();
  }

  private buildInitialParams(): { [key: string]: string } {
    const params: { [key: string]: string } = {
      id: this.data.artifactId,
      quickview: 'true'
    };
    const current = this.data.currentStartViewQuery;
    if (current) {
      const sp = new URLSearchParams(current.replace(/^\?/, ''));
      sp.forEach((value, key) => { params[key] = value; });
    }
    return params;
  }

  private parseQueryFromUrl(raw: string): { [key: string]: string } | null {
    if (!raw) return null;
    try {
      const candidate = raw.includes('://') ? raw : `https://placeholder/${raw.replace(/^\??/, '?')}`;
      const url = new URL(candidate);
      const sp = url.searchParams;
      const pan = sp.get('panorama');
      if (!pan) return null;
      const out: { [key: string]: string } = { panorama: pan };
      const h = sp.get('hlookat');
      const v = sp.get('vlookat');
      if (h !== null) out['hlookat'] = h;
      if (v !== null) out['vlookat'] = v;
      return out;
    } catch {
      return null;
    }
  }

  private composeQuery(panorama: string, h: number | undefined, v: number | undefined): string {
    const sp = new URLSearchParams();
    sp.set('panorama', panorama);
    if (h !== undefined) sp.set('hlookat', String(Math.round(h)));
    if (v !== undefined) sp.set('vlookat', String(Math.round(v)));
    return sp.toString();
  }
}
